import type { Shipment } from '@/services/api/shipments';

/** `shipments.type` as ops reads it. */
export const shipmentTypeLabel = (type: string | undefined): string | null => {
  switch (type) {
    case 'DOCKING':
      return 'Docking';
    case 'DIRECT_2W':
      return 'Direct 2W';
    case 'DIRECT_4W':
      return 'Direct 4W';
    default:
      return null;
  }
};

/** A partner shipment's handoverPoint, from its stored anchor role. */
export const handoverLabel = (
  anchorRole: Shipment['anchorRole']
): 'Delivery' | 'Retrieval' | null => {
  if (anchorRole === 'ORIGIN') return 'Delivery';
  if (anchorRole === 'DESTINATION') return 'Retrieval';
  return null;
};

/**
 * A partner-API shipment whose hub/pitstop the API could not pick (no
 * candidate near it, or core was down) — ops must set it by hand (red warning
 * + bulk "Set hub"). Every handover but DOCKING + DELIVERY is resolved at
 * create; that one waits for the hub's inbound scan, so an empty hub is
 * normal there. Imports / H2H / 4W carry no handover and never qualify.
 */
export const needsHubResolve = (
  s: Pick<Shipment, 'status' | 'type' | 'anchorRole' | 'hubId'>
): boolean => {
  if (s.status !== 'CREATED' || s.hubId != null || s.anchorRole == null) {
    return false;
  }
  return !(s.type === 'DOCKING' && s.anchorRole === 'ORIGIN');
};

/**
 * A partner-API shipment ops may (re)set the hub/pitstop of with "Set hub":
 * unresolved (CREATED), or resolved and still at the hub (AT_HUB) — moving it
 * to another one. DOCKING + DELIVERY never: its hub is where the goods were
 * scanned in. The BE has the last word (items on a rider, an open dispatch
 * preview, …) and answers per shipment.
 */
export const canSetHub = (
  s: Pick<Shipment, 'status' | 'type' | 'anchorRole'>
): boolean => {
  if (s.anchorRole == null) return false;
  if (s.status !== 'CREATED' && s.status !== 'AT_HUB') return false;
  return !(s.type === 'DOCKING' && s.anchorRole === 'ORIGIN');
};
